/* eslint-disable prefer-destructuring */
import AutoSizer from 'react-virtualized-auto-sizer';
import { useWindowSize } from 'react-use';
import { useRouter } from 'next/router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import styled from 'styled-components';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { appFetch } from 'shared/utils/appFetch';
import { getYear } from 'shared/utils/getYear';
import { WantedProductTemplatesProvider } from 'search/providers/WantedProductTemplatesProvider';
import { useLocation } from 'shared/hooks/useLocation';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useTimeline } from 'timeline/hooks/useTimeline';
import { TimelineNavigation } from 'timeline/components/TimelineNavigation';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { nonRefElementInViewport } from 'search/utils/nonRefElementInViewport';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { showOnDesktopOnly } from 'styles/utils/responsiveness';
import { SearchGridBreadcrumbs } from 'search/components/searchGrid/SearchGridBreadcrumbs';
import { setToggleCurrentTheme } from 'store/slice/pageLayoutSlice';
import { transformAggregationsToStructuredData } from 'shared/utils/consumerSearchData';
import { usePutBackHeaderOnPageChange } from 'shared/hooks/usePutBackHeaderOnPageChange';
import { DesktopFilterSidebar } from './FilterSidebar';

import styles from '../../../styles/productGrid.module.css';

import ProductGridHeader from './ProductGridHeader';
import { QUERY_NAME_DISPLAY, SORT_FILTER_MAP } from './constant';
import { getSearchFilterArray } from './utils/gridUtils';
import { MobileFilter } from './MobileFilters';
import { navigationThemes } from '../../../styles/navigation';
import VirtualizedGrid from '../virtual-grid/v2/VirtualGrid';
import { ConsumerSearchGridCell } from '../Grid/ConsumerSearchGridCell';
import { useVisibleFilters } from './hooks/useVisibleFilters';

function convertSecondsToDate(seconds) {
  const date = new Date(0); // Initialize a date object at 0 milliseconds (Epoch time)
  date.setSeconds(seconds); // Set the date to the number of seconds provided

  // if it's current yeat then return year else return MM DD
  if (date.getFullYear() === new Date().getFullYear()) {
    return `${date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    })}`;
  }
  // If the year is not the current year, return the year
  return `${date.getFullYear()}`; // Return the year
}

const PAGE_SIZE = 12;
const ItemRenderer: any = React.memo(({ data, position, timelineData }: any) => {
  if (!data) return null;

  return (
    <ConsumerSearchGridCell
      price={data?.variantsList?.[0]?.localizedLowestPriceCents?.amountCents}
      retailPrice={data.localizedRetailPriceCents?.amountCents}
      product={{
        category: data?.category,
        id: data.id,
        imageUrl: data.pictureUrl,
        productCondition: data.variantsList?.[0]?.productCondition,
        productType: data.productType,
        releaseDateDisplay: getYear({
          releaseDate: data.releaseDate,
          seasonType: data.seasonType,
          seasonYear: data.seasonYear,
          releaseYear: data.releaseDate
            ? convertSecondsToDate(data.releaseDate.seconds)
            : undefined,
          timelineData,
        }),
        slug: data.slug,
        title: data.title,
      }}
      position={position}
      disableLazyLoad
    />
  );
}, isEqual);

ItemRenderer.Price = styled.div`
  color: ${navigationThemes.light.textColor};
`;

ItemRenderer.PriceWrapper = styled.div`
  color: ${navigationThemes.light.textColor};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const queryClient = new QueryClient();
const ProductSearchGrid = ({
  toggleHeader,
  collectionSlug,
  subCollections,
  collectionDetails,
  isCrawler,
  pageFilters,
  pageAttributes = {},
  initialData,
}: any) => {
  const router = useRouter();
  const eventTrackingEventContext = useEventContext();
  usePutBackHeaderOnPageChange();

  const [isLoading, setIsLoading] = useState(false);
  const {
    query: currentQuery,
    sortType,
    categories,
    years,
    releaseDateStart,
    releaseDateEnd,
    types,
    activities,
    brands,
    colors,
    genders,
    priceMin,
    priceMax,
    prices,
    sizes,
    conditions,
    instantShip,
    underRetail,
    inStock,
    sale,
    timelineYear,
  } = router?.query || {};

  const currentPageFilter = useRef<any>(pageFilters);
  const currentPageAttributes = useRef<any>(pageAttributes);
  const [sideBarState, setSideBarState] = useState({});
  const [openFilter, setOpenFilter] = useState(
    !!pageAttributes?.isTimelinePath ||
      pageAttributes?.hideMetadata ||
      pageAttributes?.isSearchPath,
  );

  const { isHeaderVisible } = useStoreSelector((state) => state.pageLayout);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [areBreadcrumbsVisible, setAreBreadcrumbsVisible] = useState(
    !pageAttributes?.description && !pageAttributes?.hideMetadata,
  );
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const { width } = useWindowSize();
  const isMounted = useIsMounted();

  useEffect(() => {
    const desktopFilters = document.querySelector<HTMLElement>('#sidebar');
    const desktopFiltersHeight = desktopFilters?.offsetHeight
      ? desktopFilters?.offsetHeight - 160 - 78
      : 405;

    setIsSidebarVisible(desktopFiltersHeight < 400);
  }, [sideBarState]);

  useEffect(() => {
    const handleScrollEvent = () => {
      setTimeout(() => {
        if (isMounted() && width > 830) {
          const desktopFilters = document.querySelector<HTMLElement>('#sidebar');
          const desktopFiltersHeight = desktopFilters?.offsetHeight
            ? desktopFilters?.offsetHeight - 160 - 78
            : 405;

          setIsSidebarVisible(desktopFiltersHeight < 400);

          // get the position of the footer, and when it's in view
          // make sure the breadcrumbs are no longer fixed, but absolute
          const siteFooter = document.querySelector<HTMLElement>('#site-footer');
          const footerStatus = nonRefElementInViewport(siteFooter, 58);
          setIsFooterVisible(footerStatus);
        }
      }, 300);
    };

    document.addEventListener('scroll', handleScrollEvent);

    return () => {
      document.removeEventListener('scroll', handleScrollEvent);
    };
  }, []);

  const resetQuery: any = useCallback(() => {
    // TODO add pageFilters to the reset query
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument

    setSideBarState({});
    sendTrackingEvent('FILTER_RESET_TAP', {
      service_name: 'consumer_search',
      event_context: eventTrackingEventContext,
    });
    const params: any = {};
    const pathname = router.asPath?.split('?')?.[0];

    if (router.query.slug) {
      params.slug = router.query.slug;
    }

    if (currentQuery) {
      params.query = currentQuery;
    }

    void router.push({
      pathname,
      query: { ...params, pageNumber: '1' },
    });
  }, [currentQuery, router]);

  // const [mobileBarState, setMobileBarState] = useState({});

  const onSelectSection = useCallback((newValue) => {
    setSideBarState(newValue);
  }, []);

  // const onSelectMobileSection = useCallback((newValue) => {
  //   setMobileBarState(newValue);
  // }, []);

  // Functions
  function calculateColumnCount() {
    if (typeof window === 'undefined') return { columns: 4, rows: 3 }; // default to 4 columns if window is not available (e.g. SSR
    const windowWidth = window?.innerWidth;

    // You can adjust these breakpoints as needed
    if (windowWidth >= 1180) {
      return { columns: 4, rows: 3 };
    }
    if (windowWidth >= 830) {
      return { columns: 3, rows: 3 };
    }
    return { columns: 2, rows: 3 };
  }

  const [columnCount, setColumnCount] = useState(calculateColumnCount().columns);
  const { currentTheme } = useStoreSelector((state) => state.pageLayout);
  const currentThemeRef = useRef<any>('no-theme');

  const initialPage = useRef<number | undefined>(initialData?.initialPage); //TODO: Get this from the localstorage
  const [initialItems, setItems] = useState<any | null>(initialData?.items || null);
  const [aggregations, setAggregations] = useState<any | null>(initialData?.aggregations || null);
  const { selectedCountryCode } = useLocation();
  const { timelineYears } = useTimeline(pageAttributes?.timelineBreadcrumbs);
  const storeDispatch = useStoreDispatch();
  const filterVisibilityMap = useVisibleFilters(aggregations?.filters);
  const getVisibleFilters = useCallback(
    (filters) => {
      if (!filters) return {};

      return Object.fromEntries(
        Object.entries(filters).filter(([key]) => {
          if (key === 'minimumPriceCents' || key === 'maximumPriceCents') {
            return filterVisibilityMap.price;
          }
          return filterVisibilityMap[key];
        }),
      );
    },
    [filterVisibilityMap],
  );

  const initialTimelineYear = useRef<any>(pageAttributes?.timelineYear || timelineYear || null);

  useEffect(() => {
    if (
      currentThemeRef.current !== pageAttributes?.theme ||
      (!pageAttributes?.theme && currentThemeRef.current !== 'light') ||
      currentThemeRef.current === 'no-theme'
    ) {
      currentThemeRef.current = pageAttributes?.theme || 'light';
      storeDispatch(setToggleCurrentTheme(navigationThemes?.[pageAttributes?.theme || 'light']));
    }
  }, [pageAttributes?.theme]);

  const calculateRightOffset = useCallback(() => {
    if (
      (width < 830 && !pageAttributes?.hideMetadata) ||
      (width < 768 && pageAttributes?.hideMetadata)
    ) {
      return 0;
    }
    return 200;
  }, [width, pageAttributes?.hideMetadata]);

  const calculateTopOffset = useCallback(() => {
    if (collectionDetails) {
      return 584;
    }
    return 80;
  }, [collectionDetails]);

  const lastScrollTopTimeRef = useRef<any>(0);
  const lastScrollPageTimeRef = useRef<any>(0);

  const loadProductSearchData = useCallback(
    async ({
      page,
      pageSize,
      includeAggregations,
      pageCount = 1,
      isScroll = false,
    }: {
      page: number;
      pageSize: number;
      includeAggregations?: boolean;
      isScroll?: boolean;
      pageCount?: number;
    }) => {
      if (isScroll) {
        if (page === 1) {
          lastScrollTopTimeRef.current = Date.now();
        } else if (lastScrollPageTimeRef.current === 0) {
          lastScrollPageTimeRef.current = { page, pageCount, t: Date.now() };
        } else if (
          page === lastScrollPageTimeRef.current.page &&
          pageCount === lastScrollPageTimeRef.current.pageCount && // Prevent too frequent scroll requests
          Date.now() - lastScrollPageTimeRef.current.t < 1000
        ) {
          lastScrollPageTimeRef.current = 0;
          return;
        }
      } else if (
        !isScroll &&
        (initialPage.current === 1 || initialPage.current === undefined) &&
        page > 1 &&
        Date.now() - lastScrollTopTimeRef.current < 1000
      ) {
        // Prevent too frequent scroll requests
        return;
      }

      const filtersParams = {
        conditions: getSearchFilterArray(conditions as string),
        sizes: getSearchFilterArray(sizes as string),
        categories: getSearchFilterArray(categories as string),
        years: getSearchFilterArray(years as string),
        activities: getSearchFilterArray(activities as string),
        brands: getSearchFilterArray(brands as string),
        colors: getSearchFilterArray(colors as string),
        genders: getSearchFilterArray(genders as string),
        releaseDateStart:
          sortType === 'upcoming_releases' && typeof releaseDateStart === 'string'
            ? Number.parseInt(releaseDateStart, 10)
            : pageFilters?.releaseDateStart
              ? pageFilters?.releaseDateStart
              : getSearchFilterArray(releaseDateStart as string)[0]
                ? Number.parseInt(getSearchFilterArray(releaseDateStart as string)[0], 10)
                : undefined,
        releaseDateEnd:
          sortType === 'upcoming_releases' && typeof releaseDateEnd === 'string'
            ? Number.parseInt(releaseDateEnd, 10)
            : pageFilters?.releaseDateEnd
              ? pageFilters?.releaseDateEnd
              : getSearchFilterArray(releaseDateEnd as string)[0]
                ? Number.parseInt(getSearchFilterArray(releaseDateEnd as string)[0], 10)
                : undefined,
        productTypes: getSearchFilterArray(types as string),
      };

      if (pageFilters) {
        // add page filters to the filtersParams
        for (const key of Object.keys(pageFilters)) {
          if (
            pageFilters[key] &&
            typeof pageFilters[key] === 'string' &&
            key !== 'releaseDateEnd' &&
            key !== 'releaseDateStart'
          ) {
            filtersParams[key] =
              typeof pageFilters[key] !== 'string'
                ? pageFilters[key]
                : [...new Set([...(filtersParams[key] || []), pageFilters[key]])];
          }
        }
      }

      const query: any = {
        inStock,
        sale,
        salesChannelId: '1',
        queryString: (currentQuery as string) || '',
        sortType: (SORT_FILTER_MAP[sortType as string]?.value || 1).toString(),
        pageLimit: pageSize.toString(),
        pageNumber: page.toString(),
        includeAggregations: (includeAggregations || false).toString(),
        collectionSlug: collectionSlug || '',
        priceCentsMin:
          getSearchFilterArray(priceMin as string)[0] !== 'NaN'
            ? getSearchFilterArray(priceMin as string)[0]
            : undefined,
        priceCentsMax:
          getSearchFilterArray(priceMax as string)[0] !== 'NaN'
            ? getSearchFilterArray(priceMax as string)[0]
            : undefined,
        productFilter: JSON.stringify(filtersParams),
        instantShip: pageFilters?.instantShip || instantShip,
        underRetail: pageFilters?.underRetail || underRetail,
      };

      if (prices) {
        query.priceRangeCentsList = prices;
      }
      query.pageCount = pageCount;

      const url = `/get-product-search-results?${new URLSearchParams(query).toString()}`;
      const results = await appFetch(url, {
        method: 'GET',
      }).catch(() => {
        // console.log('error fetching data');
      });

      const { data, error } = results || {};

      return includeAggregations
        ? {
            error,
            data: data?.productsList || [],
            aggregation: {
              filters: transformAggregationsToStructuredData(
                data?.aggregationValuesList || [],
                selectedCountryCode,
              ),
              total: data?.totalResults,
            },
          }
        : {
            data: data?.productsList || [],
            error,
          };
    },
    [
      currentQuery,
      sortType,
      years,
      categories,
      types,
      activities,
      brands,
      colors,
      priceMax,
      priceMin,
      prices,
      genders,
      releaseDateEnd,
      releaseDateStart,
      collectionSlug,
      sizes,
      conditions,
      instantShip,
      underRetail,
      inStock,
      sale,
      selectedCountryCode,
      pageFilters,
    ],
  );

  async function fetchDataAndScroll() {
    setIsLoading(true);

    const initPage = Number.parseInt((router.query.pageNumber as string) || '1', 10);
    if (initPage === null) return;

    const totalPagesToFake = initPage + 2;
    const loadedPages = Array.from({ length: 5 }, (_, i) => initPage - 2 + i).filter((n) => n >= 1);
    const minLoaded = Math.min(...loadedPages);

    const allPagesToSimulate = Array.from({ length: totalPagesToFake }, (_, i) => i + 1);

    const pages = [];
    const pageParams = [];

    try {
      const result = await loadProductSearchData({
        page: minLoaded,
        pageSize: PAGE_SIZE,
        includeAggregations: true,
        pageCount: loadedPages.length,
        isScroll: true,
      });

      const allData = result?.data || [];
      const totalAgg = result?.aggregation?.total || 0;

      for (const page of allPagesToSimulate) {
        const isLoaded = loadedPages.includes(page);

        let items: any[] = [];
        if (isLoaded) {
          const offset = page - minLoaded;
          const start = offset * PAGE_SIZE;
          const end = start + PAGE_SIZE;
          items = allData.slice(start, end);
        } else {
          items = Array.from({ length: PAGE_SIZE }, () => ({}));
        }

        const start = (page - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE;

        pages.push({
          page,
          items,
          prevPage: page > 1 ? page - 1 : undefined,
          nextPage: end < totalAgg ? page + 1 : undefined,
          hasMore: end < totalAgg,
          isPlaceholder: !isLoaded,
        });

        pageParams.push(page);
      }

      initialPage.current = result?.data?.length > 12 ? initPage : 1;

      setItems({
        pages,
        pageParams,
        hasData: result?.data?.length > 0,
      });

      setAggregations(result?.aggregation);
      //  scroll to the 1st item of the currentPage
      // which is the number of rows for one page + 2 since there are 3 rows on a view
      // TODO scroll to the last visible element

      setIsLoading(false);
    } catch {
      initialPage.current = 1;
      // console.log(error);
      setItems({});
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (
      initialTimelineYear.current !== pageAttributes?.timelineYear &&
      pageAttributes?.isTimelinePath
    ) {
      setIsLoading(true);

      // if the timeline
      fetchDataAndScroll();
      initialTimelineYear.current = pageAttributes?.timelineYear;
    }
  }, [initialTimelineYear, pageAttributes?.timelineYear, pageAttributes?.isTimelinePath]);

  useEffect(() => {
    // if (!gridRef.current) return;
    // Fetch initial data or restore scroll position

    if (initialItems === null) {
      fetchDataAndScroll();
    }
  }, [initialItems]);

  useEffect(() => {
    if (initialItems === null) {
      fetchDataAndScroll();
    }
  }, []);

  useEffect(() => {
    const newSidebarState: any = {};
    // underline what needs to be underlined
    for (const name of Object.values(QUERY_NAME_DISPLAY)) {
      if (router?.query?.[name]) {
        newSidebarState[name] = true;
      }
    }

    // open brands filter when on timeline page
    if (pageAttributes?.isTimelinePath) {
      newSidebarState.brandsList = true;
    }

    setSideBarState(newSidebarState);
  }, []);

  const lastQueryRef = useRef({ ...(router?.query || {}), pageNumber: 1 });
  // anytime the query changes change the grid
  useEffect(() => {
    const newQauery = { ...(router?.query || {}), pageNumber: 1 };
    const queryChanged = !isEqual(newQauery, lastQueryRef.current);
    const filtersChanged = !isEqual(currentPageFilter.current, pageFilters);
    const attributesChanged = !isEqual(currentPageAttributes.current, pageAttributes);

    if (initialItems !== null && (queryChanged || filtersChanged || attributesChanged)) {
      lastQueryRef.current = newQauery;
      currentPageFilter.current = pageFilters;
      currentPageAttributes.current = pageAttributes;
      setItems(null);
    }
  }, [router.query, pageFilters, pageAttributes, initialItems]);

  useEffect(() => {
    // Handle window resize to recalculate column count
    function handleResize() {
      const { columns } = calculateColumnCount();
      setColumnCount(columns);
      // here check the items size if smaller then load the new items coming in
    }
    const debouncedHandleResize = debounce(handleResize, 200);

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  // Render an individual item within the grid

  const getGridHeader = useCallback(
    (options?: any) => {
      const currentOffset = calculateRightOffset();

      const isDescription =
        !options?.isMobile &&
        (collectionDetails || pageAttributes?.description) &&
        !pageAttributes?.hideMetadata;

      return (
        <div>
          <ProductGridHeader
            total={aggregations?.total}
            collectionDetails={collectionDetails?.data}
            pageAttributes={pageAttributes}
            offset={{
              right: currentOffset ? (isDescription ? 132 : currentOffset + 64) : 16,
              left: currentOffset ? 0 : 16,
            }}
            isMobile={options?.isMobile}
            resetQuery={resetQuery}
            hideTotal={options?.hideTotal}
          />
        </div>
      );
    },
    [aggregations?.total, calculateRightOffset, collectionDetails, pageAttributes, resetQuery],
  );

  useEffect(() => {
    document.addEventListener('scroll', () => {
      const scroll = window.scrollY;

      const header: any = document.querySelector('#sidebar');
      if (header) {
        if (scroll > 100) {
          // header.style.position = 'fixed';
          header.style.top = '0';
          header.style.zIndex = '1000';
          header.style.width = '100%';
        } else {
          header.style.position = 'relative';
        }
      }
    });
  }, []);

  const sidebarContainer = useRef<HTMLDivElement | null>(null);

  // NOTE: The AutoSizer component requires JavaScript to be enabled
  // This is a workaround to render the grid for people with JavaScript disabled (crawlers)
  const isJavaScriptEnabled = typeof window !== 'undefined';
  const isServerSide = !isJavaScriptEnabled;
  if (isServerSide) {
    return (
      <div
        style={{
          width: '100%',
          height: '100svh',
          position: 'relative',
          paddingTop: 0,
        }}
      >
        <div
          data-qa="search_grid_container"
          style={{
            width: '100%',
            height: '100svh',
            paddingLeft: 0,
            borderTop: 'none',
          }}
        >
          <div className="server-side-header">
            {getGridHeader({ isMobile: false, hideTotal: true })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <WantedProductTemplatesProvider>
        <div
          className={styles.wrapper}
          style={{
            marginTop: pageAttributes?.hideMetadata ? -42 : 0, // 64px for the header
          }}
        >
          <AutoSizer>
            {({ height: gridHeight, width: gridWidth }: { height: number; width: number }) => {
              // offset right 137 to create the space on the left
              // - 8 margin for scroll because the filter bar is 178px wide
              // offset right == 137 side bar width + 13 margin scrollbar
              return (
                <div
                  style={{
                    width: gridWidth,
                    height: gridHeight,
                    position: 'relative',
                    paddingTop: pageAttributes?.hideMetadata ? (width < 768 ? 48 : 94) : 0,
                  }}
                >
                  {(pageAttributes?.hideMetadata && width >= 768) ||
                  (!pageAttributes?.hideMetadata && width >= 830) ? (
                    <div
                      className="no-scrollbar tw-overflow-y-scroll"
                      id="sidebar-container"
                      ref={sidebarContainer}
                      style={{
                        width: calculateRightOffset(),
                        position: 'fixed',
                        height: `100vh`,
                        zIndex: '1',
                        paddingTop: pageAttributes?.isTimelinePath
                          ? 65
                          : pageAttributes?.description &&
                              !pageAttributes?.isTimelinePath &&
                              !pageAttributes?.hideMetadata &&
                              !pageAttributes?.isSearchPath
                            ? 445
                            : 0,
                        background: 'transparent',
                        transition: 'padding-top 0.3s ease',
                      }}
                    >
                      <DesktopFilterSidebar
                        resetQuery={resetQuery}
                        onSelectSection={onSelectSection}
                        sideBarState={sideBarState}
                        filters={getVisibleFilters(aggregations?.filters || {})}
                        pageFilters={pageFilters}
                        hideMetadata={pageAttributes?.hideMetadata}
                        subCollections={subCollections}
                        timelineData={{
                          isTimelinePath: pageAttributes.isTimelinePath,
                          timelineType: pageAttributes.timelineType,
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="tw-border-0 tw-border-b tw-border-solid"
                      style={{
                        height:
                          pageAttributes?.brandStoryVisible || pageAttributes?.isSearchPath
                            ? 135
                            : pageAttributes?.isTimelinePath
                              ? '152px'
                              : openFilter
                                ? '140px'
                                : pageAttributes?.description
                                  ? '0'
                                  : '140px',
                        overflow: 'hidden',
                        transition: 'height 0.3s ease',
                        borderBottom:
                          openFilter || (!openFilter && !pageAttributes?.description)
                            ? `1px solid ${currentTheme?.borderColor || '#000000'}`
                            : 'none',
                      }}
                    >
                      {getGridHeader({ isMobile: true })}
                      <MobileFilter
                        filters={getVisibleFilters(aggregations?.filters || {})}
                        isCrawler={isCrawler}
                        timelineData={{
                          isTimelinePath: pageAttributes.isTimelinePath,
                          timelineType: pageAttributes.timelineType,
                        }}
                      />
                      {pageAttributes?.isTimelinePath && (
                        <TimelineNavigation
                          timelineYears={timelineYears}
                          showTimelineNavigationOnMobile={isHeaderVisible}
                          noMargins
                        />
                      )}
                    </div>
                  )}
                  <div
                    data-qa="search_grid_container"
                    style={{
                      width: pageAttributes?.hideMetadata ? gridWidth - 90 : gridWidth,
                      height: gridHeight,
                      paddingLeft: calculateRightOffset(),
                      borderTop:
                        !pageAttributes?.hideMetadata || width < 768
                          ? 'none'
                          : `1px solid ${currentTheme.borderColor || '#000000'}`,
                    }}
                  >
                    <VirtualizedGrid
                      totalNumberOfItems={aggregations?.total || 0}
                      initialPages={initialItems?.pages}
                      startingPage={initialPage?.current || 1}
                      initialPageParams={!initialItems ? null : initialItems?.pageParams}
                      width={gridWidth}
                      height={gridHeight}
                      rowsPerPage={gridHeight <= 780 ? 2 : 3}
                      columnCount={columnCount}
                      offset={{
                        left:
                          calculateRightOffset() +
                          (!pageAttributes?.hideMetadata && width >= 830 ? 64 : 0), // 64px for empty space on the right
                        top: calculateTopOffset(),
                      }}
                      queryKeys={[
                        currentQuery,
                        sortType,
                        years,
                        categories,
                        types,
                        activities,
                        brands,
                        colors,
                        priceMax,
                        priceMin,
                        prices,
                        genders,
                        releaseDateEnd,
                        releaseDateStart,
                        collectionSlug,
                        sizes,
                        conditions,
                        instantShip,
                        underRetail,
                        inStock,
                        sale,
                        selectedCountryCode,
                        pageFilters,
                      ]}
                      ItemRenderer={ItemRenderer}
                      timelineData={{
                        isTimelinePath: pageAttributes.isTimelinePath,
                        timelineType: pageAttributes.timelineType,
                      }}
                      fetchItems={loadProductSearchData}
                      hideMetadata={pageAttributes?.hideMetadata}
                      getGridHeader={
                        width >= 830 ||
                        (pageAttributes?.description && !pageAttributes?.hideMetadata) ||
                        (pageAttributes?.isBrandsPath && pageAttributes?.description)
                          ? getGridHeader
                          : () => null
                      }
                      onScrollGrid={(scrollTop) => {
                        setAreBreadcrumbsVisible(scrollTop > 180);
                        if (
                          (pageAttributes?.description &&
                            !pageAttributes?.isTimelinePath &&
                            !pageAttributes?.hideMetadata &&
                            !pageAttributes?.isSearchPath) ||
                          pageAttributes?.isBrandsPath
                        ) {
                          setOpenFilter(scrollTop !== 0);
                          // scroll to the top of the sidebar container
                          const sidebarContainers = sidebarContainer.current;
                          if (sidebarContainers) {
                            // scroll to the top of the sidebar container
                            sidebarContainers.style.paddingTop = `${Math.max(0, 445 - scrollTop)}px`;
                          }
                        }
                      }}
                      toggleHeader={toggleHeader}
                    />

                    {initialItems &&
                      !initialItems?.hasData &&
                      !isLoading &&
                      (pageAttributes?.isTimelinePath ? (
                        <div className="tw-overflow-auto tw-h-screen tw-w-full tw-relative no-scrollbar">
                          {getGridHeader({ isMobile: false })}
                          <div className="tw-mt-4 tw-text-center tw-fixed tw-top-[300px] tw-w-[calc(100%_-_50px)] md:tw-w-[calc(100%_-_300px)]">
                            <p className="tw-m-4">
                              There were no matching results. Please try a different search or reset
                              your filters.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="tw-absolute tw-top-0 tw-flex tw-justify-center tw-items-center tw-h-[calc(100%_-_300px)] tw-md:tw-h-[50%] tw-w-[calc(100%_-_50px)] md:tw-w-[calc(100%_-_300px)]">
                          <p className="tw-m-4">
                            There were no matching results. Please try a different search or reset
                            your filters.
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              );
            }}
          </AutoSizer>
          <ProductSearchGrid.BreadcrumbContainer
            id="grid-breadcrumbs"
            $isOverlapping={isFooterVisible}
            $isVisible={areBreadcrumbsVisible && isSidebarVisible}
          >
            <SearchGridBreadcrumbs breadcrumbs={pageAttributes?.breadcrumbs} />
          </ProductSearchGrid.BreadcrumbContainer>
        </div>
      </WantedProductTemplatesProvider>
    </QueryClientProvider>
  );
};

ProductSearchGrid.BreadcrumbContainer = styled.div<{
  $isOverlapping?: boolean;
  $isVisible: boolean;
}>`
  ${({ $isOverlapping }) => showOnDesktopOnly`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
    ${$isOverlapping ? `position: absolute;` : `position: fixed;`}
    text-align: right;
    bottom: 0;
    z-index: 10;
    width: 180px;
    padding: 0 16px 32px;
  `};

  transition: all 0.3s;
  transition-timing-function: ease-in;

  ${({ $isVisible, $isOverlapping }) =>
    $isVisible
      ? `opacity: 1; ${
          $isOverlapping
            ? `
    bottom: 0px;`
            : `
    bottom: 36px;`
        } `
      : 'opacity: 0; bottom: -300px;'}
`;

ProductSearchGrid.MobileBreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  margin-bottom: 72px;
  margin-top: 24px;
  padding-left: 16px;
`;

ProductSearchGrid.GridTop = styled.div<{ $backgroundColor?: string; $isBannerVisible: boolean }>`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    background-color: ${({ theme, $backgroundColor }) =>
      $backgroundColor || theme.navigation.backgroundColor};
    border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
    display: block;
    height: 27px;
  }
`;

export default ProductSearchGrid;
